import { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LOGO_URL } from "../lib/brand";
import { Lock, Mail, ArrowRight, AlertCircle, KeyRound, X, CheckCircle2 } from "lucide-react";
import { MailDeliveryHint } from "../components/MailDeliveryHint";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function PartnerLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get("next");
  const nextTarget = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/partner/login`, { email, password }, { withCredentials: true });
      // JWT liegt im httpOnly-Cookie — localStorage hält nur einen Marker.
      localStorage.setItem("alphalane_partner_token", "1");
      localStorage.setItem("alphalane_partner_meta", JSON.stringify(r.data.partner));
      localStorage.removeItem("alphalane_admin_token");
      if (r.data.partner?.must_change_password) {
        if (nextTarget) sessionStorage.setItem("alphalane_login_next", nextTarget);
        navigate("/partner/change-password");
      } else {
        navigate(nextTarget || "/partner/coaching");
      }
    } catch (error) {
      setErr("Ungültige Zugangsdaten.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center p-5 relative overflow-hidden">
      <div className="al-blob" style={{ top: "-150px", right: "-100px", width: 480, height: 480, background: "radial-gradient(closest-side, rgba(168,85,247,0.4), transparent)" }} />
      <div className="al-blob" style={{ bottom: "-150px", left: "-100px", width: 480, height: 480, background: "radial-gradient(closest-side, rgba(74,143,231,0.4), transparent)" }} />

      <div className="relative al-card w-full max-w-md p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Alphalane" className="h-10 w-10 object-contain" />
          <div className="font-display">
            <div className="font-semibold text-base"><span className="al-grad-text">ALPHALANE</span></div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Partner-Login</div>
          </div>
        </div>

        <h1 className="mt-7 font-display font-bold text-3xl leading-tight">
          <span className="al-grad-text">Willkommen zurück.</span>
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Logge dich mit deinen Partner-Zugangsdaten ein.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="partner-login-form">
          <label className="block">
            <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
              <Mail className="h-3.5 w-3.5" /> E-Mail
            </span>
            <input
              data-testid="partner-login-email"
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              className="al-input"
              placeholder="dein@partner-email.com"
            />
          </label>
          <label className="block">
            <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
              <Lock className="h-3.5 w-3.5" /> Passwort
            </span>
            <input
              data-testid="partner-login-password"
              type="password" required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="al-input"
              placeholder="••••••••••••"
            />
          </label>

          {err && (
            <div className="flex items-center gap-2 text-sm text-rose-600">
              <AlertCircle className="h-4 w-4" /> {err}
            </div>
          )}

          <button
            type="submit"
            data-testid="partner-login-submit"
            disabled={loading}
            className="al-cta w-full justify-center disabled:opacity-60"
          >
            {loading ? "Prüfe..." : "Einloggen"} <ArrowRight className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => setShowForgot(true)}
            data-testid="partner-forgot-password-link"
            className="w-full text-center text-xs text-slate-500 hover:text-[var(--al-purple)] transition-colors mt-1 inline-flex items-center justify-center gap-1.5"
          >
            <KeyRound className="h-3 w-3" /> Passwort vergessen?
          </button>
        </form>

        <p className="mt-6 text-xs text-slate-400 text-center">
          Noch keine Zugangsdaten? Wende dich an Pascal.
        </p>
      </div>

      {showForgot && <ForgotPasswordModal initialEmail={email} onClose={() => setShowForgot(false)} />}

      <style>{`
        .al-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 15px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 4px rgba(139,92,246,0.12); }
      `}</style>
    </div>
  );
}

function ForgotPasswordModal({ initialEmail, onClose }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/partner/forgot-password`, { email });
      setDone(true);
    } catch (_) {
      // Generic message even on error to avoid email enumeration.
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="partner-forgot-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8 relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Schliessen"
          data-testid="partner-forgot-close"
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {!done ? (
          <>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-2xl bg-violet-100 text-[var(--al-purple)] flex items-center justify-center">
                <KeyRound className="h-5 w-5" />
              </div>
              <h2 className="font-display font-bold text-xl">Passwort vergessen?</h2>
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Gib deine Partner-E-Mail ein. Wir generieren ein neues Passwort und schicken es dir per Mail zu —
              danach kannst du dich wieder einloggen.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="partner-forgot-form">
              <label className="block">
                <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                  <Mail className="h-3.5 w-3.5" /> E-Mail
                </span>
                <input
                  data-testid="partner-forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  className="al-input"
                  placeholder="dein@partner-email.com"
                />
              </label>

              {err && (
                <div className="flex items-center gap-2 text-sm text-rose-600">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              )}

              <button
                type="submit"
                data-testid="partner-forgot-submit"
                disabled={busy}
                className="al-cta w-full justify-center disabled:opacity-60"
              >
                {busy ? "Sende…" : "Neues Passwort anfordern"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </>
        ) : (
          <div data-testid="partner-forgot-success" className="text-center py-3">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="mt-4 font-display font-bold text-xl">Check deine Mailbox</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Falls die E-Mail bei uns hinterlegt ist, schicken wir dir gleich ein neues Passwort.
            </p>
            <MailDeliveryHint testid="partner-forgot-mail-hint" />
            <button
              type="button"
              onClick={onClose}
              data-testid="partner-forgot-done"
              className="al-cta w-full justify-center mt-5"
            >
              Zurück zum Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
